import { useMutation, useQueryClient } from 'react-query';
import type { PaymentMethodViewDto } from '@subscriptions/application/dtos';
import { getSubscriptionBlockUseCases } from '@subscriptions/composition';

/**
 * Variables accepted by the attach-payment-method mutation.
 *
 * `paymentMethodUuid` identifies the saved org payment method selected in the
 * saved-cards step; the attach is idempotent so re-attaching the block's
 * current method succeeds.
 */
export type AttachBlockPaymentMethodVariables = {
  blockUuid: string;
  paymentMethodUuid: string;
};

/**
 * Hook for attaching a saved org payment method to a subscription block
 * (`POST /v1/subscription-block/{uuid}/attach-payment-method`).
 *
 * Resolves with the attached {@link PaymentMethodViewDto} that feeds the
 * `card-updated` confirmation. On success, invalidates the block detail and
 * blocks list queries so the new method is reflected when leaving the wizard.
 *
 * @returns React Query mutation result
 */
export const useAttachBlockPaymentMethod = () => {
  const queryClient = useQueryClient();
  const { attachPaymentMethod } = getSubscriptionBlockUseCases();

  return useMutation<PaymentMethodViewDto, Error, AttachBlockPaymentMethodVariables>(
    ({ blockUuid, paymentMethodUuid }) => attachPaymentMethod.execute(blockUuid, paymentMethodUuid),
    {
      onSuccess: (_, { blockUuid }) => {
        queryClient.invalidateQueries(['subscriptions', 'blocks', 'detail', blockUuid]);
        queryClient.invalidateQueries(['subscriptions', 'blocks', 'list']);
      },
    },
  );
};
